import React, { useMemo } from 'react';
import MetricView from '../common/MetricView';
import ShiftHeadcountExpanded from './ShiftHeadcountExpanded';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import Heading from '@amzn/meridian/heading';
import { BUSINESS_VIEW_NAME, SSD_FEATURE_FLAGS_LIST } from '../../../../../utils/constants';
import { displayBusinessTabHeader, getBusiness, isFeatureFlagEnabled } from '../../../utils';

const PROCESS_PATH_IDLE = 'idle';

const getBusinessHeadcountData = (headcountAllocationPivot, shift, plan = null) => {
  const baseHeadcounts = { core: 0, grocery: 0, idle: 0 };
  if (!shift || !headcountAllocationPivot?.length) return baseHeadcounts;

  const shiftData = headcountAllocationPivot.filter(
    (entry) => entry.start_datetime_local >= shift.start && entry.start_datetime_local < shift.end
  );

  const headcounts = {
    core: { totalHeadcount: 0, shiftsCount: 0 },
    grocery: { totalHeadcount: 0, shiftsCount: 0 },
    idle: { totalHeadcount: 0, shiftsCount: 0 }
  };

  for (const shiftEntry of shiftData) {
    let shiftHeadcounts = { ...baseHeadcounts };

    for (const [key, value] of Object.entries(shiftEntry)) {
      const keyBusiness = getBusiness(key, plan);
      const targetBusiness = keyBusiness || (key.startsWith(PROCESS_PATH_IDLE) ? 'idle' : null);

      if (targetBusiness) {
        shiftHeadcounts[targetBusiness] += value;
      }
    }

    // Update business data for non-zero shifts
    for (const [business, count] of Object.entries(shiftHeadcounts)) {
      if (count > 0) {
        headcounts[business].totalHeadcount += count;
        headcounts[business].shiftsCount++;
      }
    }
  }

  const getAverageCount = (business) => {
    const { totalHeadcount, shiftsCount } = headcounts[business];
    return shiftsCount === 0 ? 0 : Math.floor(totalHeadcount / shiftsCount);
  };

  return {
    core: getAverageCount('core'),
    grocery: getAverageCount('grocery'),
    idle: getAverageCount('idle')
  };
};

const getIdleHC = (shift, headcountAllocation) => {
  return shift
    ? headcountAllocation.find(
        ({ start_datetime_local, process_path }) =>
          start_datetime_local >= shift.start && start_datetime_local < shift.end && process_path === PROCESS_PATH_IDLE
      )
    : false;
};

const ShiftHeadcount = ({
  shift,
  headcountAllocation,
  headcountAllocationPivot,
  business = BUSINESS_VIEW_NAME.CORE,
  isGroceryEnabled = false,
  plan = null
}) => {
  const isRushIndirectRulesEnabled = isFeatureFlagEnabled(
    plan?.input?.adminSettings,
    SSD_FEATURE_FLAGS_LIST.rushIndirectRulesEnabled
  );

  const businessHeadcount = useMemo(() => {
    return getBusinessHeadcountData(headcountAllocationPivot, shift, plan);
  }, [headcountAllocationPivot, shift, plan]);

  const displayHeadcount = business === BUSINESS_VIEW_NAME.GROCERY ? businessHeadcount.grocery : businessHeadcount.core;
  const values = [{ title: 'Total Needed (avg)', subtext: displayHeadcount }];
  if (business === BUSINESS_VIEW_NAME.CORE) {
    values.push({ title: 'Total Idle (avg)', subtext: businessHeadcount.idle });
  }

  return (
    <MetricView
      headerComponent={
        <Row width="100%" alignmentHorizontal="justify">
          <Heading level={2} type="h300">
            <Text>{displayBusinessTabHeader(business)} Headcount</Text>
          </Heading>
          <ShiftHeadcountExpanded
            shift={shift}
            headcountAllocationPivot={headcountAllocationPivot}
            business={business}
            headcountAllocation={headcountAllocation}
            isGroceryEnabled={isGroceryEnabled}
            isRushIndirectRulesEnabled={isRushIndirectRulesEnabled}
            plan={plan}
          />
        </Row>
      }
      values={values}
    />
  );
};

export default ShiftHeadcount;
export { getIdleHC };
